import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { getPurchaseCreditNotes } from '../../services/PurchaseCreditNoteServices';
import { displayError } from '../../helpers';
import { fetchAndShowPurchaseCreditNotePdf } from '../../utils/purchaseCreditNotePdf';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllVendors } from '../../services/VendorServices';
import {
    getDateRangeFromPurchaseSettings,
    getPurchaseSettingsSummary,
    loadPurchaseListSettings,
    resolvePlantFilter,
    resolveVendorFilter,
    savePurchaseListSettings,
} from '../../utils/purchaseListSettings';

function PurchaseCreditNoteTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [purchaseSettings, setPurchaseSettings] = useState(() => loadPurchaseListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromPurchaseSettings(purchaseSettings),
        [purchaseSettings],
    );

    const fetchNotes = useCallback(() => {
        const plantId = resolvePlantFilter(purchaseSettings, plants);
        const vendorIds = resolveVendorFilter(purchaseSettings, vendors);
        const [rangeFrom, rangeTo] = activeDateRange;

        if (!plantId || !vendorIds || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }

        setIsLoading(true);
        return getPurchaseCreditNotes(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
            vendor_ids: vendorIds,
        })
            .then((response) => {
                setData({
                    records: (response.purchase_credit_note_list || []).map((row) => ({
                        ...row,
                        status_label: row.status === 'Active' || row.status === true || row.status === 1
                            ? 'Active'
                            : 'Cancelled',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load purchase credit notes.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, purchaseSettings, vendors]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'cn_no', headerName: 'Credit Note No' },
        { fieldName: 'cn_date', headerName: 'Credit Note Date' },
        { fieldName: 'vendor_name', headerName: 'Vendor Name' },
        { fieldName: 'cn_type_label', headerName: 'Credit Note Type' },
        { fieldName: 'voucher_mode', headerName: 'Voucher Mode' },
        { fieldName: 'reference_no', headerName: 'GRN Reference' },
        { fieldName: 'credit_amt', headerName: 'Amount' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return data.records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return data.records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [data.records, searchText]);

    const summaryStats = useMemo(() => {
        const activeCount = filteredRecords.filter((row) => row.status_label === 'Active').length;
        const totalAmount = filteredRecords.reduce((sum, row) => sum + (parseFloat(row.credit_amt) || 0), 0);
        return [
            { label: 'ACTIVE', value: String(activeCount), accent: 'b2b' },
            { label: 'TOTAL AMOUNT', value: totalAmount.toFixed(2), accent: 'b2c' },
        ];
    }, [filteredRecords]);

    const settingsSummary = useMemo(
        () => getPurchaseSettingsSummary(purchaseSettings, plants, vendors),
        [purchaseSettings, plants, vendors],
    );

    const onEdit = (noteId) => {
        navigate(`/PurchaseCreditNoteEdit/${noteId}/`);
    };

    const onPrint = (noteId, event) => {
        event.preventDefault();
        setIsLoading(true);
        fetchAndShowPurchaseCreditNotePdf(cookies, noteId, { title: 'Purchase Credit Note PDF' })
            .finally(() => setIsLoading(false));
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
            filteredRecords,
            'PurchaseCreditNote',
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Purchase Credit Note',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            filteredRecords,
            'PurchaseCreditNote.pdf',
        );
    };

    const handleSettingsSave = (nextSettings) => {
        savePurchaseListSettings(cookies.myUserId, nextSettings);
        setPurchaseSettings(nextSettings);
        setShowSettings(false);
        Swal.fire('Updated!', '', 'success');
    };

    useEffect(() => {
        Promise.all([getUserAllowedPlants(cookies), getAllVendors(cookies)])
            .then(([plantList, vendorList]) => {
                setPlants((plantList.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
                setVendors((vendorList.vendor_list || [])
                    .filter((vendor) => vendor.status)
                    .map((vendor) => ({ value: vendor.id, label: vendor.entity_name })));
                setIsInitialized(true);
            })
            .catch(() => setIsLoading(false));
    }, [cookies]);

    useEffect(() => {
        if (!isInitialized || plants.length === 0) {
            return;
        }
        fetchNotes();
    }, [isInitialized, plants.length, vendors.length, purchaseSettings, activeDateRange, fetchNotes]);

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="PURCHASE"
                    title="List of Purchase Credit Note"
                    subtitle="Review vendor credit notes against GRN for purchase return and adjustments."
                    modeLabel="VIEW MODE"
                    recordCount={filteredRecords.length}
                    recordLabel="purchase credit notes"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    formLink="/PurchaseCreditNote"
                    addNewLabel="Add New"
                >
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="List of Purchase Credit Note"
                                columns={columns}
                                data={filteredRecords}
                                onRowEdit={onEdit}
                                onRowPrint={onPrint}
                                hideDelete
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                hidePrint={false}
                                autoPinActionColumn={false}
                                compactRows
                            />
                        </div>
                    </div>
                </ListPageLayout>

                <SalesListSettingsModal
                    show={showSettings}
                    allowedPlants={plants}
                    customers={vendors}
                    initialSettings={purchaseSettings}
                    onSave={handleSettingsSave}
                    onClose={() => setShowSettings(false)}
                />
            </div>
        </div>
    );
}

export default PurchaseCreditNoteTable;
